import { DurationFormat } from "@formatjs/intl-durationformat"
import {
  DateFormatter,
  isSameDay,
  now,
  parseAbsolute,
  ZonedDateTime,
} from "@internationalized/date"
import { useQuery } from "@tanstack/react-query"
import { ReservationStatus } from "ipctg-inflow-types/InFlowEnums"
import type { ReservationMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { useEffect, useState } from "react"
import { ProgressBar } from "react-aria-components"
import { Trans, useTranslation } from "react-i18next"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import {
  getLocale,
  isCancelledStatus,
  toDuration,
  UNKNOWN_PLATE_PREFIX,
} from "@/utilities/etc"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import { normalizeLicensePlate } from "@/utilities/normalizers"
import { scopedTo } from "@/utilities/scopedRoutes"

import styles from "./ReservationCard.module.css"
import { CancelModal } from "./reservationModals/CancelModal"
import { InfoModal } from "./reservationModals/InfoModal"
import { VehicleModal } from "./reservationModals/VehicleModal"

interface Props {
  reservation: ReservationMini
  variant?: "confirmation"
  onSelectPlate?: (plate: string) => void
  onCancelReservation?: () => Promise<void>
  isCancelling?: boolean
  qrLookupID?: string
  contextLocationID?: string | null
}

export function ReservationConfirmationCard({
  reservation,
  variant,
  onSelectPlate,
  onCancelReservation,
  isCancelling,
  qrLookupID,
  contextLocationID,
}: Props) {
  const { t, i18n } = useTranslation("reserve")
  const locale = i18n.resolvedLanguage || getLocale()
  const timezoneLocation = reservation.locationTimezone

  const sessionStart = parseAbsolute(
    reservation.startDateTime,
    timezoneLocation
  )
  const sessionEnd = parseAbsolute(reservation.endDateTime, timezoneLocation)

  const dateFormatter = new DateFormatter(locale, {
    month: "short",
    day: "numeric",
    timeZone: timezoneLocation,
  })
  const timeFormatter = new DateFormatter(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezoneLocation,
  })

  const endsOnStartDate = isSameDay(sessionStart, sessionEnd)
  const startDate = dateFormatter.format(sessionStart.toDate())
  const endDate = dateFormatter.format(sessionEnd.toDate())
  const startTime = timeFormatter.format(sessionStart.toDate())
  const endTime = timeFormatter.format(sessionEnd.toDate())

  const calendarName = reservation.bookingCalendarName
  const rateName = reservation.inventoryTreeRateName
  const licensePlate = reservation.plate

  const [progression, setProgression] = useState(() =>
    sessionProgression(sessionStart, sessionEnd)
  )
  const [canChangePlate, setCanChangePlate] = useState(() =>
    checkCanChangePlate(reservation.allowPlateChangeUntil, timezoneLocation)
  )

  const { data: vehicleNickname } = useQuery({
    ...meQueryOptions(),
    select: (me) =>
      me?.inflowUser.savedPlates.find(
        (v) =>
          normalizeLicensePlate(v.plate) === normalizeLicensePlate(licensePlate)
      )?.nickname,
  })

  const [vehicleModalOpen, toggleVehicleModal] = useToggle(false)
  const [infoModalOpen, toggleInfoModal] = useToggle(false)
  const [cancelModalOpen, toggleCancelModal] = useToggle(false)

  useEffect(() => {
    const start = parseAbsolute(reservation.startDateTime, timezoneLocation)
    const end = parseAbsolute(reservation.endDateTime, timezoneLocation)
    const update = () => {
      setProgression(sessionProgression(start, end))
      setCanChangePlate(
        checkCanChangePlate(reservation.allowPlateChangeUntil, timezoneLocation)
      )
    }
    update()

    const current = now(timezoneLocation)
    const msUntilNextMinute = (60 - current.second) * 1000 - current.millisecond
    let interval: ReturnType<typeof setInterval> | null = null
    const timeout = setTimeout(() => {
      update()
      interval = setInterval(update, 60_000)
    }, msUntilNextMinute)

    return () => {
      clearTimeout(timeout)
      if (interval !== null) clearInterval(interval)
    }
  }, [
    reservation.startDateTime,
    reservation.endDateTime,
    reservation.allowPlateChangeUntil,
  ])

  const sessionEnded = now(timezoneLocation).compare(sessionEnd) >= 0
  const isUpcoming = now(timezoneLocation).compare(sessionStart) < 0
  const hasPlate =
    !!licensePlate && !licensePlate.startsWith(UNKNOWN_PLATE_PREFIX)

  const cardStatus = getStatus({ status: reservation.status, isUpcoming })
  const isInactive = cardStatus === "expired" || cardStatus === "canceled"

  // UI flags
  const isConfirmation = variant === "confirmation"
  const showVehicleButton = canChangePlate && !isConfirmation && !isInactive
  const showStatusTag = isInactive
  const showTimeRemaining = !isConfirmation
  const showActionButton = !isConfirmation
  const showProgressBar = !isInactive && !isUpcoming && !isConfirmation
  const refundReceiptID =
    cardStatus === "canceled" && reservation.receiptIDs.length >= 2
      ? reservation.receiptIDs[1]
      : null
  const receiptID = refundReceiptID || reservation.receiptIDs[0]
  const useThemedReceipt =
    !!qrLookupID &&
    !!contextLocationID &&
    contextLocationID === reservation.locationID

  const statusLabel =
    cardStatus === "canceled"
      ? t("confirmation.status.canceled")
      : t("confirmation.status.completed")

  return (
    <article className={styles.card} data-status={cardStatus}>
      {/* Upcoming current: tappable vehicle card with swap/warning icon */}
      {showVehicleButton ? (
        <button
          className={styles.vehicleCard}
          onClick={toggleVehicleModal}
          type="button"
        >
          <div
            className={styles.vehicleIconWrapper}
            data-plate={hasPlate ? "valid" : "missing"}
          >
            <Icon name="car" />
          </div>
          <div className={styles.vehicleInfo}>
            {hasPlate ? (
              <>
                {vehicleNickname && (
                  <span className="body--1 bold">{vehicleNickname}</span>
                )}
                <span className="body--2">{licensePlate}</span>
              </>
            ) : (
              <>
                <span className="body--2 bold">
                  {t("confirmation.enterVehicle")}
                </span>
                <span className="body--3">
                  {t("confirmation.vehiclePlate")}
                </span>
              </>
            )}
          </div>
          <Icon name={hasPlate ? "swap" : "alert"} />
        </button>
      ) : (
        /* Active current, past, and confirmation: static plate header with optional status tag */
        <header className={styles.header}>
          <Icon name="car" />
          <Heading level={2}>
            <span className={styles.licensePlate}>
              {hasPlate ? licensePlate : t("confirmation.noPlateAdded")}
            </span>
          </Heading>
          {showStatusTag && (
            <span className={styles.statusTag} data-status={cardStatus}>
              {statusLabel}
            </span>
          )}
        </header>
      )}

      {/* Always shown: calendar name and rate */}
      <Heading level={2} as={3} className={styles.calendarName}>
        {calendarName}
      </Heading>
      <span className={styles.rateName}>{rateName}</span>

      <section className={styles.mainContent}>
        <div className={styles.cardBody}>
          {/* Always shown: park from / until window */}
          <div className={styles.details}>
            <span className={styles.timeRange}>
              <Trans
                i18nKey="cart.parkFrom"
                ns="reserve"
                values={{ date: startDate, time: startTime }}
                components={{ value: <span className="semi-bold" /> }}
              />
            </span>
            <span className={styles.timeRange}>
              <Trans
                i18nKey={
                  endsOnStartDate ? "cart.parkUntilSameDay" : "cart.parkUntil"
                }
                ns="reserve"
                values={{ date: endDate, time: endTime }}
                components={{ value: <span className="semi-bold" /> }}
              />
            </span>
          </div>
          {/* Current and past only: time remaining or status label */}
          {showTimeRemaining && (
            <span className={styles.timeRemaining}>
              {isInactive ? statusLabel : timeRemainingCopy()}
            </span>
          )}
        </div>

        {/* Current and past only: receipt link (past) or info button (current) */}
        {showActionButton &&
          (isInactive ? (
            <LinkButton
              className={styles.receiptButton}
              buttonType="default"
              {...scopedTo(
                "/receipt/$receiptID",
                useThemedReceipt ? qrLookupID : undefined,
                { receiptID }
              )}
              aria-label={t("confirmation.viewReceipt")}
            >
              <Icon name="receipt" />
            </LinkButton>
          ) : (
            <Button
              className={styles.infoButton}
              buttonType="default"
              onPress={toggleInfoModal}
              isPending={isCancelling}
              aria-label={t("confirmation.reservationDetails")}
            >
              <Icon name="infoOutlined" />
            </Button>
          ))}
      </section>

      {/* Active current only: session progress bar */}
      {showProgressBar && (
        <ProgressBar
          className={styles.progressBar}
          aria-label={timeRemainingCopy()}
          value={progression.elapsed}
          maxValue={progression.total}
        >
          {({ percentage }) => (
            <div className={styles.track}>
              <div
                className={styles.fill}
                style={{ "--percent": `${percentage || 100}%` }}
              />
            </div>
          )}
        </ProgressBar>
      )}

      {/* Modals: vehicle swap, reservation info, cancel confirmation */}
      <VehicleModal
        isOpen={vehicleModalOpen}
        onOpenChange={toggleVehicleModal}
        onSelectPlate={onSelectPlate}
      />
      <InfoModal
        isOpen={infoModalOpen}
        onOpenChange={toggleInfoModal}
        reservation={reservation}
        onOpenCancelModal={toggleCancelModal}
        qrLookupID={qrLookupID}
        contextLocationID={contextLocationID}
      />
      <CancelModal
        isOpen={cancelModalOpen}
        onOpenChange={toggleCancelModal}
        reservation={reservation}
        onConfirmCancel={onCancelReservation}
        isPending={isCancelling}
      />
    </article>
  )

  function sessionProgression(start: ZonedDateTime, end: ZonedDateTime) {
    const total = end.compare(start)
    const current = now(timezoneLocation)
    const elapsed = Math.min(total, Math.max(0, current.compare(start)))
    return { total, elapsed }
  }

  function timeRemainingCopy(): string {
    const current = now(timezoneLocation)
    const notStarted = current.compare(sessionStart) < 0

    if (sessionEnded) {
      return t("confirmation.status.completed")
    }

    const diffMs = notStarted
      ? sessionStart.compare(current)
      : sessionEnd.compare(current)

    const totalMinutes = diffMs / 1000 / 60

    if (notStarted && totalMinutes >= 24 * 60) {
      const days = Math.ceil(totalMinutes / (24 * 60))
      return t("confirmation.beginsInDays", { count: days })
    }

    const durationStr = new DurationFormat(locale, { style: "narrow" }).format(
      toDuration({
        intervalCount: totalMinutes,
        intervalPeriod: "Minute",
      })
    )

    return notStarted
      ? t("confirmation.beginsIn", { time: durationStr })
      : t("confirmation.timeLeft", { time: durationStr })
  }
}

function checkCanChangePlate(
  allowPlateChangeUntil: string | null,
  timezoneLocation: string
): boolean {
  if (!allowPlateChangeUntil) return false
  return (
    now(timezoneLocation).compare(
      parseAbsolute(allowPlateChangeUntil, timezoneLocation)
    ) < 0
  )
}

type CardStatus = "upcoming" | "active" | "expired" | "canceled"

function getStatus({
  status,
  isUpcoming,
}: {
  status: ReservationStatus
  isUpcoming: boolean
}): CardStatus {
  if (isCancelledStatus(status)) return "canceled"

  switch (status) {
    case ReservationStatus.Expired:
      return "expired"
    case ReservationStatus.Active:
    default:
      return isUpcoming ? "upcoming" : "active"
  }
}
